/**
 * Leadler › Öğrenme blocks (C3 learning design section 4): scorecard, what changed this
 * week, the learning batch summary, arms, vocabulary, cells, fit, verification quality,
 * mail and data health. Every number shows its n and interval, and "Tahmin yok" below the
 * minimum n. Lists never approve: the batch is decided on its own detail screen.
 */

import { type ReactNode, useEffect, useId, useState } from 'react';
import { EVAL_OUTCOME_LABELS } from '../api/evals.js';
import {
  ARM_STATUS_LABELS,
  type ArmRow,
  CHANGE_CLASS_LABELS,
  type ChangeClass,
  type Estimate,
  FIT_LABEL_LABELS,
  FIT_LABELS,
  type FitLabel,
  type FitLabelTask,
  LABEL_SOURCE_LABELS,
  type LearningChange,
  type LearningLoop,
  type LearningView,
  LOCALITY_LABELS,
  LOOP_LABELS,
  type MetricUnit,
  type ScorecardTile,
  TERM_ORIGIN_LABELS,
  TERM_SLOT_LABELS,
  TERM_STATUS_LABELS,
} from '../api/learning.js';
import {
  canRevert,
  hasEstimate,
  NEVER_REVERTED,
  regionLowerBound,
  regionReportable,
  revertLoosens,
  showsFitScore,
} from '../api/learning-rules.js';
import { MIN_REASON_LENGTH, reasonText } from '../api/policy-rules.js';
import { REJECT_REASON_LABELS } from '../api/types.js';
import { usePanel, useStore } from '../app/context.js';
import { useEvals } from '../app/evals.js';
import { useLeads } from '../app/leads.js';
import { Link } from '../app/router.js';
import { formatAge, formatDayMonth, formatNumber, formatRelativeDay, formatUsd } from '../lib/format.js';
import { formatInterval, formatMetric, formatN, NO_ESTIMATE, trendWord } from '../lib/learning-format.js';
import { POLICY_HOLD_MS, T4HoldButton } from './HoldButton.js';
import { Icon } from './icons.js';
import { SectionHeader } from './primitives.js';
import { ChoiceChips, Sheet } from './Sheet.js';

// ---- small parts ------------------------------------------------------------

/** Value, interval and n; "Tahmin yok" and the n below the minimum. */
export function EstimateValue({
  estimate,
  unit,
  compact = false,
}: {
  estimate: Estimate;
  unit: MetricUnit;
  compact?: boolean;
}) {
  if (!hasEstimate(estimate)) {
    return (
      <span className="lr-est lr-est--none">
        <span className="lr-none">{NO_ESTIMATE}</span>
        <span className="lr-est__n mono">{formatN(estimate)}</span>
      </span>
    );
  }
  const interval = formatInterval(estimate, unit);
  return (
    <span className={compact ? 'lr-est lr-est--compact' : 'lr-est'}>
      <span className="lr-est__value mono">{formatMetric(estimate.value, unit, estimate.n)}</span>
      {interval ? <span className="lr-est__ci">{interval}</span> : null}
      <span className="lr-est__n mono">{formatN(estimate)}</span>
    </span>
  );
}

/** A1 and A2 outlined, H1 ink, H2 dashed (release), H3 accent with a lock (T4). */
export function ClassChip({ changeClass }: { changeClass: ChangeClass }) {
  return (
    <span className={`lr-class lr-class--${changeClass.toLowerCase()}`} title={CHANGE_CLASS_LABELS[changeClass]}>
      {changeClass === 'H3' ? <Icon name="lock" size={10} stroke={2.5} /> : null}
      {changeClass}
      <span className="sr-only">, {CHANGE_CLASS_LABELS[changeClass]}</span>
    </span>
  );
}

function LoopKey({ loop }: { loop: LearningLoop }) {
  return (
    <span className="key ink-2 lr-loop">
      {loop} · {LOOP_LABELS[loop]}
    </span>
  );
}

function Block({
  id,
  title,
  aside,
  children,
  wide = false,
}: {
  id: string;
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <section className={wide ? 'lr-block lr-block--wide' : 'lr-block'} aria-labelledby={id}>
      <SectionHeader id={id} title={title} right={aside ? <span className="lr-block__aside">{aside}</span> : null} />
      {children}
    </section>
  );
}

function Note({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'warning' }) {
  return (
    <p className={`lr-note lr-note--${tone}`}>
      <Icon name="info" size={14} className="lr-note__icon" />
      <span>{children}</span>
    </p>
  );
}

function TableWrap({ label, children }: { label: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable by keyboard.
    <section className="lr-table-wrap" aria-label={label} tabIndex={0}>
      {children}
    </section>
  );
}

// ---- 1. scorecard -----------------------------------------------------------

function TrendBars({ tile }: { tile: ScorecardTile }) {
  const current = hasEstimate(tile.estimate) ? tile.estimate.value : null;
  const values = [...tile.trend, current];
  const known = values.filter((value): value is number => value !== null);
  const max = Math.max(...known, 0);
  const word = trendWord(tile.trend, current);
  return (
    <span className="lr-trend">
      <span className="lr-trend__bars" aria-hidden="true">
        {values.map((value, index) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed five-week series, position is the identity.
            key={index}
            className={value === null ? 'lr-trend__bar lr-trend__bar--none' : 'lr-trend__bar'}
            style={{ height: value === null || max === 0 ? '2px' : `${Math.max(2, (value / max) * 16)}px` }}
          />
        ))}
      </span>
      <span className="lr-trend__word">{word ? `4 hafta: ${word}` : '4 hafta: veri az'}</span>
    </span>
  );
}

export function ScorecardBlock({ view }: { view: LearningView }) {
  return (
    <Block id="lr-karne" title="Karne" aside={<span className="key ink-2">{view.week}</span>} wide>
      <ul className="lr-tiles" aria-label="Karne">
        {view.scorecard.map((tile) => (
          <li key={tile.id} className="lr-tile">
            <span className="lr-tile__label">{tile.label}</span>
            <EstimateValue estimate={tile.estimate} unit={tile.unit} compact />
            <TrendBars tile={tile} />
          </li>
        ))}
      </ul>
    </Block>
  );
}

// ---- 2. what changed this week ----------------------------------------------

function RevertSheet({ change, onClose }: { change: LearningChange; onClose: () => void }) {
  const { store } = useLeads();
  const panel = useStore();
  const [reason, setReason] = useState('');
  const loosens = revertLoosens(change);
  const reasonOk = reasonText(reason).length >= MIN_REASON_LENGTH;
  const blocker = store.decisionBlocker();
  const disabledReason = blocker ?? (reasonOk ? null : 'Önce gerekçe yaz');
  const run = (confirmedByHold: boolean) => {
    const text = reasonText(reason);
    onClose();
    void store.revert(change.changeId, { reason: text, confirmedByHold });
  };
  return (
    <Sheet
      title={`Geri al: ${change.objectLabel}`}
      description={
        loosens
          ? 'Geri almak aramayı genişletir: gerekçe yaz ve 1,5 sn basılı tut. Gerekçe öğrenme kaydına yazılır.'
          : 'Gerekçe öğrenme kaydına yazılır; öğrenici bu nesneyi bir hafta yeniden değiştirmez.'
      }
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Vazgeç
          </button>
          {loosens ? (
            <T4HoldButton
              compact
              label="Geri al"
              sublabel="1,5 sn basılı tut"
              holdVerb="geri almak"
              durationMs={POLICY_HOLD_MS}
              disabledReason={disabledReason}
              onComplete={() => run(true)}
              onEarlyRelease={() => panel.showToast('Geri almak için 1,5 sn basılı tut', { icon: 'info' })}
            />
          ) : (
            <button
              type="button"
              className="btn btn--primary"
              disabled={disabledReason !== null}
              onClick={() => run(false)}
            >
              {disabledReason ?? 'Geri al'}
            </button>
          )}
        </>
      }
    >
      <dl className="loosen__change">
        <div>
          <dt className="key ink-2">Şimdi</dt>
          <dd className="mono">{change.after}</dd>
        </div>
        <Icon name="arrow-right" size={16} className="ink-3" />
        <div>
          <dt className="key ink-2">Geri alınınca</dt>
          <dd className="mono loosen__to">{change.before}</dd>
        </div>
      </dl>
      <p className="loosen__effect">{change.why}</p>
      <label className="field">
        <span className="field__label">Gerekçe · zorunlu, öğrenme kaydına yazılır</span>
        <textarea
          className="field__input"
          rows={3}
          value={reason}
          aria-required="true"
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
    </Sheet>
  );
}

function ChangeStatus({ change, now, onRevert }: { change: LearningChange; now: number; onRevert: () => void }) {
  if (change.revertedAt !== null) {
    return (
      <span className="lr-change__state">
        <Icon name="history" size={14} />
        Geri alındı · {change.revertedBy ?? 'kurul'} · {formatRelativeDay(change.revertedAt, now)}
      </span>
    );
  }
  if (canRevert(change)) {
    return (
      <button
        type="button"
        className="btn btn--secondary btn--small lr-change__revert"
        aria-label={`Geri al: ${change.objectLabel}${revertLoosens(change) ? ', gevşetir, basılı tut ve gerekçe yaz' : ''}`}
        onClick={onRevert}
      >
        {revertLoosens(change) ? <Icon name="timer" size={12} className="ink-2" /> : null}
        Geri al
      </button>
    );
  }
  return (
    <span className="lr-change__state">
      <Icon name="lock" size={12} />
      {NEVER_REVERTED.has(change.objectType) ? 'Ekleme amaçlı, geri alınmaz' : 'Geri alınamaz'}
    </span>
  );
}

export function ChangesBlock({ view }: { view: LearningView }) {
  const { now } = usePanel();
  const [reverting, setReverting] = useState<LearningChange | null>(null);
  const changes = [...view.changes].sort((a, b) => b.appliedAt - a.appliedAt);
  return (
    <Block id="lr-degisti" title="Bu hafta ne değişti" aside={<span className="key ink-2">A1 · A2</span>}>
      {view.flags.learningAutoapply ? null : (
        <Note>
          Otomatik uygulama kapalı (feature.learning_autoapply). A2 değişiklikleri uygulanmaz, öneri olarak bekler.
        </Note>
      )}
      {changes.length === 0 ? (
        <p className="placeholder-text">Bu hafta otomatik değişiklik yok.</p>
      ) : (
        <ul className="lr-changes" aria-label="Otomatik değişiklikler">
          {changes.map((change) => (
            <li key={change.changeId} className={change.revertedAt ? 'lr-change lr-change--reverted' : 'lr-change'}>
              <div className="lr-change__head">
                <ClassChip changeClass={change.changeClass} />
                <LoopKey loop={change.loop} />
                <span className="lr-change__time mono">{formatRelativeDay(change.appliedAt, now)}</span>
              </div>
              <p className="lr-change__title">{change.objectLabel}</p>
              <p className="lr-change__diff">
                <span className="mono">{change.before}</span>
                <Icon name="arrow-right" size={14} className="ink-3" />
                <span className="mono">{change.after}</span>
              </p>
              <p className="lr-change__why">{change.why}</p>
              {change.evidence ? (
                <p className="lr-change__evidence">
                  <EstimateValue estimate={change.evidence} unit="share" compact />
                </p>
              ) : null}
              <div className="lr-change__foot">
                <span className="key ink-3">{change.appliedBy}</span>
                <ChangeStatus change={change} now={now} onRevert={() => setReverting(change)} />
              </div>
            </li>
          ))}
        </ul>
      )}
      {reverting ? <RevertSheet change={reverting} onClose={() => setReverting(null)} /> : null}
    </Block>
  );
}

// ---- 3. waiting for your approval (summary only; decided on the batch screen) ----

export function PendingBlock({ view }: { view: LearningView }) {
  const batch = view.batch;
  return (
    <Block id="lr-bekleyen" title="Onayını bekleyen öğrenmeler" aside={<span className="key ink-2">H1 · H2 · H3</span>}>
      {!batch || batch.proposals.length === 0 ? (
        <p className="placeholder-text">Bekleyen öğrenme yok. Sonraki paket pazartesi 08:00 raporuyla gelir.</p>
      ) : (
        <>
          <p className="lr-pending__sum">
            <span className="mono">{batch.proposals.length}</span> öneri · tahmini{' '}
            <span className="mono">{batch.estimatedMinutes}</span> dk · hedef{' '}
            <span className="mono">{batch.targetMinutes}</span> dk · son gün{' '}
            <span className="mono">{formatDayMonth(batch.expiresAt)}</span>
          </p>
          <ul className="lr-pending" aria-label="Paketteki öneriler">
            {batch.proposals.map((proposal) => (
              <li key={proposal.proposalId} className="lr-pending__row">
                <ClassChip changeClass={proposal.changeClass} />
                <span className="lr-pending__title">{proposal.title}</span>
              </li>
            ))}
          </ul>
          <Link to="/leadler/ogrenme/paket" className="btn btn--primary lr-pending__go">
            Paketi incele
            <Icon name="chevron-right" size={16} />
          </Link>
        </>
      )}
    </Block>
  );
}

// ---- 4. arms ----------------------------------------------------------------

function uniq(values: string[]): string[] {
  return [...new Set(values)].sort();
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
  labels,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  labels?: Readonly<Record<string, string>>;
}) {
  const id = useId();
  return (
    <span className="lr-filter">
      <label htmlFor={id} className="key ink-2">
        {label}
      </label>
      <select id={id} className="lr-filter__select" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Tümü</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels?.[option] ?? option}
          </option>
        ))}
      </select>
    </span>
  );
}

function armTitle(arm: ArmRow): string {
  return `${arm.template} · ${arm.source} · ${arm.terms.join(' + ')}`;
}

export function ArmsBlock({ view }: { view: LearningView }) {
  const { now } = usePanel();
  const [template, setTemplate] = useState('');
  const [source, setSource] = useState('');
  const [locality, setLocality] = useState('');
  const rows = view.arms.filter(
    (arm) =>
      (template === '' || arm.template === template) &&
      (source === '' || arm.source === source) &&
      (locality === '' || arm.localityKind === locality),
  );
  return (
    <Block id="lr-kollar" title="Kollar" aside={<LoopKey loop="L2" />} wide>
      <div className="lr-filters">
        <FilterSelect
          label="Şablon"
          value={template}
          options={uniq(view.arms.map((arm) => arm.template))}
          onChange={setTemplate}
        />
        <FilterSelect
          label="Kaynak"
          value={source}
          options={uniq(view.arms.map((arm) => arm.source))}
          onChange={setSource}
        />
        <FilterSelect
          label="Yer türü"
          value={locality}
          options={uniq(view.arms.map((arm) => arm.localityKind))}
          labels={LOCALITY_LABELS}
          onChange={setLocality}
        />
      </div>
      <TableWrap label="Kollar tablosu">
        <table className="lr-table">
          <thead>
            <tr>
              <th scope="col">Kol</th>
              <th scope="col">Bölge</th>
              <th scope="col" className="num">
                Çalıştırma
              </th>
              <th scope="col" className="num">
                Başarı
              </th>
              <th scope="col">Sonsal ortalama</th>
              <th scope="col">Durum</th>
              <th scope="col">Son</th>
              <th scope="col" className="num">
                Başarı başı
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((arm) => (
              <tr key={`${arm.armKey}@${arm.region}`}>
                <th scope="row" className="lr-table__name">
                  {armTitle(arm)}
                  <span className="lr-table__sub">
                    {arm.locale} · {LOCALITY_LABELS[arm.localityKind]} · {TERM_ORIGIN_LABELS[arm.termOrigin]}
                  </span>
                </th>
                <td>{arm.region}</td>
                <td className="num mono">{arm.runs}</td>
                <td className="num mono">{arm.successes}</td>
                <td>
                  <EstimateValue estimate={arm.posterior} unit="share" compact />
                </td>
                <td>
                  <span className={`lr-status lr-status--${arm.status}`}>{ARM_STATUS_LABELS[arm.status]}</span>
                </td>
                <td className="mono">{arm.lastRunAt === null ? 'yok' : formatRelativeDay(arm.lastRunAt, now)}</td>
                <td className="num mono">{arm.costPerSuccess === null ? 'yok' : formatUsd(arm.costPerSuccess)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      {rows.length === 0 ? <p className="placeholder-text">Bu süzgeçle kol yok.</p> : null}
    </Block>
  );
}

// ---- 5. vocabulary ----------------------------------------------------------

export function VocabularyBlock({ view }: { view: LearningView }) {
  return (
    <Block id="lr-kelime" title="Kelime dağarcığı" aside={<LoopKey loop="L2" />}>
      {view.vocabulary.map((locale) => (
        <div key={locale.locale} className="lr-vocab">
          <h3 className="lr-sub">
            {locale.locale} · sürüm <span className="mono">{locale.version}</span>
          </h3>
          <p className="lr-vocab__counts">
            <span className="mono">{locale.active}</span> etkin · <span className="mono">{locale.candidate}</span> aday
            · <span className="mono">{locale.retired}</span> emekli · <span className="mono ink-3">{locale.hash}</span>
          </p>
          <dl className="lr-pairs">
            <div>
              <dt>Öğrenilmiş terim verimi</dt>
              <dd>
                <EstimateValue estimate={locale.learnedYield} unit="share" compact />
              </dd>
            </div>
            <div>
              <dt>Tohum terim verimi</dt>
              <dd>
                <EstimateValue estimate={locale.seedYield} unit="share" compact />
              </dd>
            </div>
          </dl>
          <ul className="lr-terms" aria-label={`${locale.locale} terimleri`}>
            {locale.terms.map((term) => (
              <li key={term.termId} className="lr-term">
                <details>
                  <summary className="lr-term__summary">
                    <span className="lr-term__text" lang="en">
                      {term.term}
                    </span>
                    <span className={`lr-status lr-status--${term.status}`}>{TERM_STATUS_LABELS[term.status]}</span>
                    <span className="key ink-2">
                      {TERM_SLOT_LABELS[term.slot]} · {TERM_ORIGIN_LABELS[term.origin]}
                    </span>
                    {term.region ? (
                      <span className="lr-region" title="Sadece bu bölgede kullanılır">
                        <Icon name="map-pin" size={12} />
                        {term.region}
                      </span>
                    ) : (
                      <span className="lr-region lr-region--all">tüm {term.locale}</span>
                    )}
                  </summary>
                  <div className="lr-term__body">
                    <p>
                      Verim: <EstimateValue estimate={term.yield} unit="share" compact />
                      {term.lift !== null ? (
                        <>
                          {' '}
                          · lift <span className="mono">{formatNumber(term.lift, 1)}</span>
                        </>
                      ) : null}
                    </p>
                    <ol className="lr-history" aria-label={`${term.term} geçmişi`}>
                      {term.history.map((entry) => (
                        <li key={`${entry.at}-${entry.status}`}>
                          <span className="mono">{formatDayMonth(entry.at)}</span> · {TERM_STATUS_LABELS[entry.status]}{' '}
                          · {entry.note}
                        </li>
                      ))}
                    </ol>
                  </div>
                </details>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </Block>
  );
}

// ---- 6. cells ---------------------------------------------------------------

function SaturationSpark({ points, label }: { points: { queries: number; newQualified: number }[]; label: string }) {
  const maxX = Math.max(1, ...points.map((point) => point.queries));
  const maxY = Math.max(1, ...points.map((point) => point.newQualified));
  const path = points
    .map(
      (point) =>
        `${((point.queries / maxX) * 96 + 2).toFixed(1)},${(30 - (point.newQualified / maxY) * 26).toFixed(1)}`,
    )
    .join(' ');
  return (
    <svg className="lr-spark" viewBox="0 0 100 32" role="img" aria-label={label}>
      <line x1="2" y1="30" x2="98" y2="30" className="lr-spark__axis" />
      <polyline points={path} className="lr-spark__line" />
    </svg>
  );
}

export function CellsBlock({ view }: { view: LearningView }) {
  const cells = view.cells;
  return (
    <Block id="lr-hucreler" title="Hücreler" aside={<LoopKey loop="L1" />} wide>
      <h3 className="lr-sub">Doygunluk</h3>
      <ul className="lr-sat" aria-label="Doygunluk eğrileri">
        {cells.saturation.map((curve) => {
          const last = curve.points[curve.points.length - 1];
          const text = `${curve.cellId}: ${last?.queries ?? 0} sorguda ${last?.newQualified ?? 0} yeni nitelikli, ${
            curve.saturated ? 'doydu' : 'doymadı'
          }, K ${curve.satK}`;
          return (
            <li key={curve.cellId} className="lr-sat__item">
              <SaturationSpark points={curve.points} label={text} />
              <span className="lr-sat__text">
                <span className="mono">{curve.cellId}</span> · {curve.region}
                <span className="lr-table__sub">
                  {last?.queries ?? 0} sorgu · {last?.newQualified ?? 0} yeni nitelikli · K {curve.satK} ·{' '}
                  {curve.saturated ? 'doydu' : 'devam'}
                </span>
              </span>
            </li>
          );
        })}
      </ul>

      <h3 className="lr-sub">Sıradaki hücreler</h3>
      <ol className="lr-frontier" aria-label="Sıradaki hücreler">
        {cells.frontier.map((row) => {
          const c = row.components;
          return (
            <li key={row.cellId} className="lr-frontier__row">
              <div className="lr-frontier__head">
                <span className="mono lr-frontier__rank">{row.rank}.</span>
                <span className="mono">{row.cellId}</span>
                <span className="ink-2">{row.region}</span>
                {row.holdout ? <span className="lr-status lr-status--holdout">Kontrol %10</span> : null}
                <span className="mono lr-frontier__score">{formatNumber(row.score, 3)}</span>
              </div>
              <p className="lr-frontier__why">Neden bu hücre: {row.reason}</p>
              <p className="lr-frontier__parts">
                komşu verimi <span className="mono">{formatNumber(c.neighbourYield, 3)}</span> = ({c.shrinkK} ×{' '}
                <span className="mono">{formatNumber(c.prior, 3)}</span> + {c.neighbourQualified}) / ({c.shrinkK} +{' '}
                {c.neighbourQueries}) · talep <span className="mono">{formatNumber(c.demand, 1)}</span> · çapa{' '}
                <span className="mono">{c.anchor}</span> · halka <span className="mono">{c.ringDistance}</span>
              </p>
            </li>
          );
        })}
      </ol>

      <dl className="lr-pairs">
        <div>
          <dt>Kontrol (%10) verimi</dt>
          <dd>
            <EstimateValue estimate={cells.holdoutYield} unit="share" compact />
          </dd>
        </div>
        <div>
          <dt>Sınır verimi</dt>
          <dd>
            <EstimateValue estimate={cells.frontierYield} unit="share" compact />
          </dd>
        </div>
      </dl>

      <h3 className="lr-sub">Bölge tahmini</h3>
      <ul className="lr-list" aria-label="Bölge tahminleri">
        {cells.regionEstimates.map((estimate) => (
          <li key={estimate.region} className="lr-list__row">
            <span className="lr-list__name">{estimate.region}</span>
            {regionReportable(estimate) ? (
              <span>
                yaklaşık <span className="mono">{formatNumber(estimate.estimate ?? 0)}</span> şirket
                {estimate.ciLow !== null && estimate.ciHigh !== null ? (
                  <span className="ink-2">
                    {' '}
                    · %95 aralık <span className="mono">{estimate.ciLow}</span> ile{' '}
                    <span className="mono">{estimate.ciHigh}</span>
                  </span>
                ) : null}
                <span className="ink-2 mono"> · m {estimate.m}</span>
              </span>
            ) : (
              <span>
                <span className="lr-none">{NO_ESTIMATE}</span> alt sınır{' '}
                <span className="mono">{regionLowerBound(estimate)}</span>
                <span className="ink-2 mono"> · m {estimate.m}, en az 7</span>
              </span>
            )}
          </li>
        ))}
      </ul>

      <h3 className="lr-sub">Sektörler</h3>
      <ul className="lr-list" aria-label="Sektörler">
        {cells.sectors.map((sector) => (
          <li key={`${sector.region}-${sector.sector}`} className="lr-list__row">
            <span className="lr-list__name">
              {sector.region} · {sector.sector}
            </span>
            <span className="ink-2">
              <span className="mono">{sector.judgedCells}</span> değerlendirilmiş hücre ·{' '}
              {sector.status === 'open'
                ? 'açık'
                : sector.status === 'stopped'
                  ? 'durduruldu'
                  : 'durdurma önerildi (H1, en az 3 hücre)'}
            </span>
          </li>
        ))}
      </ul>
    </Block>
  );
}

// ---- 7. fit (report only) ---------------------------------------------------

function LabelSheet({ task, onClose }: { task: FitLabelTask; onClose: () => void }) {
  const { store } = useLeads();
  const [label, setLabel] = useState<FitLabel | null>(null);
  const blocker = store.decisionBlocker();
  return (
    <Sheet
      title={`Etiketle: ${task.company}`}
      description={
        showsFitScore(task)
          ? 'Etiket uyum formülünü değiştirmez; haftalık raporda ölçülür.'
          : 'Kör örnek: uyum puanı ve katman bu ekranda gizli. Etiketin puandan bağımsız ölçülür.'
      }
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={label === null || blocker !== null}
            onClick={() => {
              if (!label) return;
              onClose();
              void store.labelFit(task.taskId, label);
            }}
          >
            {blocker ?? 'Kaydet'}
          </button>
        </>
      }
    >
      <p className="lr-label__meta">
        <span className="mono">{task.domain}</span> · {task.region}
      </p>
      {showsFitScore(task) && task.fitScore !== null ? (
        <p className="lr-label__score" data-testid="fit-score">
          Uyum <span className="mono">{task.fitScore}</span>
          {task.tier ? (
            <>
              {' '}
              · katman <span className="mono">{task.tier}</span>
            </>
          ) : null}
        </p>
      ) : null}
      <ul className="lr-label__evidence" aria-label="Kanıt">
        {task.evidence.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <ChoiceChips label="Etiket" options={FIT_LABELS} labels={FIT_LABEL_LABELS} value={label} onChange={setLabel} />
    </Sheet>
  );
}

export function FitBlock({ view }: { view: LearningView }) {
  const fit = view.fit;
  const [labelling, setLabelling] = useState<FitLabelTask | null>(null);
  return (
    <Block id="lr-uyum" title="Uyum" aside={<LoopKey loop="L3" />} wide>
      <Note tone="warning">
        Sadece rapor. Uyum formülü öğrenmeyle değişmez
        {fit.formulaDecisionPending ? '; formül kararı (satış planı, PR #21) bekleniyor' : ''}. Yeni sürüm 2 hafta
        gölgede puanlanır ve sürüm PR'ı ile gelir (H2).
      </Note>
      <TableWrap label="Katman ve sonuç tablosu">
        <table className="lr-table">
          <thead>
            <tr>
              <th scope="col">Katman</th>
              {FIT_LABELS.map((label) => (
                <th key={label} scope="col" className="num">
                  {FIT_LABEL_LABELS[label]}
                </th>
              ))}
              <th scope="col">İyi payı (Wilson)</th>
            </tr>
          </thead>
          <tbody>
            {fit.tierOutcome.map((row) => (
              <tr key={row.tier}>
                <th scope="row" className="mono">
                  {row.tier}
                </th>
                {FIT_LABELS.map((label) => (
                  <td key={label} className="num mono">
                    {row.counts[label]}
                  </td>
                ))}
                <td>
                  <EstimateValue estimate={row.good} unit="share" compact />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>

      <h3 className="lr-sub">Bileşen ilişkisi</h3>
      <dl className="lr-pairs">
        {fit.components.map((component) => (
          <div key={component.component}>
            <dt>{component.component}</dt>
            <dd>
              <EstimateValue estimate={component.association} unit="auc" compact />
            </dd>
          </div>
        ))}
      </dl>

      <h3 className="lr-sub">Etkin ve gölge sürüm</h3>
      <TableWrap label="Uyum sürümleri">
        <table className="lr-table">
          <thead>
            <tr>
              <th scope="col">Sürüm</th>
              <th scope="col">AUC</th>
              <th scope="col">İlk 20 isabet</th>
            </tr>
          </thead>
          <tbody>
            {fit.versions.map((version) => (
              <tr key={version.version}>
                <th scope="row">
                  {version.version}
                  <span className="lr-table__sub">{version.status === 'active' ? 'etkin' : 'gölge'}</span>
                </th>
                <td>
                  <EstimateValue estimate={version.auc} unit="auc" compact />
                </td>
                <td>
                  <EstimateValue estimate={version.top20Precision} unit="share" compact />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>

      <dl className="lr-pairs">
        <div>
          <dt>Etiket kaynağı</dt>
          <dd>
            {fit.labelsBySource.map((item, index) => (
              <span key={item.source}>
                {index > 0 ? ' · ' : ''}
                {LABEL_SOURCE_LABELS[item.source]} <span className="mono">{item.n}</span>
              </span>
            ))}
          </dd>
        </div>
        <div>
          <dt>Katalog boşluğu (yeni tarama adayı)</dt>
          <dd>
            {fit.catalogGap.map((gap, index) => (
              <span key={gap.vehicle}>
                {index > 0 ? ' · ' : ''}
                {gap.vehicle}{' '}
                <span className="mono">
                  {gap.companies}/{gap.of}
                </span>
              </span>
            ))}
          </dd>
        </div>
      </dl>

      <h3 className="lr-sub">Etiket bekleyen</h3>
      {fit.labelTasks.length === 0 ? (
        <p className="placeholder-text">Etiket bekleyen lead yok.</p>
      ) : (
        <ul className="lr-list" aria-label="Etiket bekleyen leadler">
          {fit.labelTasks.map((task) => (
            <li key={task.taskId} className="lr-list__row">
              <span className="lr-list__name">
                {task.company}
                <span className="lr-table__sub mono">{task.domain}</span>
              </span>
              <span className="ink-2">
                {showsFitScore(task) && task.fitScore !== null ? (
                  <>
                    uyum <span className="mono">{task.fitScore}</span>
                    {task.tier ? <span className="mono"> · {task.tier}</span> : null}
                  </>
                ) : (
                  'kör örnek · puan gizli'
                )}
              </span>
              <button
                type="button"
                className="btn btn--secondary btn--small lr-label__btn"
                aria-label={`Etiketle: ${task.company}`}
                onClick={() => setLabelling(task)}
              >
                Etiketle
              </button>
            </li>
          ))}
        </ul>
      )}
      {labelling ? <LabelSheet task={labelling} onClose={() => setLabelling(null)} /> : null}
    </Block>
  );
}

// ---- 8. verification quality ------------------------------------------------

export function VerificationBlock({ view }: { view: LearningView }) {
  const v = view.verification;
  return (
    <Block id="lr-dogrulama" title="Doğrulama kalitesi" aside={<LoopKey loop="L4" />} wide>
      <TableWrap label="Kural bazında uyuşmazlık">
        <table className="lr-table">
          <thead>
            <tr>
              <th scope="col">Kural</th>
              <th scope="col">Ajan ile hakem</th>
              <th scope="col">Ajan ile sen</th>
            </tr>
          </thead>
          <tbody>
            {v.disagreement.map((row) => (
              <tr key={row.rule}>
                <th scope="row" className="mono lr-table__rule">
                  {row.rule}
                </th>
                <td>
                  <EstimateValue estimate={row.agentVsReferee} unit="share" compact />
                </td>
                <td>
                  <EstimateValue estimate={row.agentVsSercan} unit="share" compact />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      <dl className="lr-pairs">
        <div>
          <dt>Denetim alan hatası</dt>
          <dd>
            <EstimateValue estimate={v.auditFieldError} unit="share" compact />
          </dd>
        </div>
        <div>
          <dt>Teslim edilmemeli</dt>
          <dd>
            <span className="mono">{v.mustNotDeliver}</span> / <span className="mono">{v.auditedRecords}</span> kayıt
          </dd>
        </div>
        <div>
          <dt>ECE</dt>
          <dd>
            <EstimateValue estimate={v.ece} unit="auc" compact />
          </dd>
        </div>
        <div>
          <dt>Brier</dt>
          <dd>
            <EstimateValue estimate={v.brier} unit="auc" compact />
          </dd>
        </div>
        <div>
          <dt>Regresyon</dt>
          <dd>
            <span className="mono">
              {v.regression.passed}/{v.regression.total}
            </span>{' '}
            · gizli set{' '}
            <span className="mono">
              {v.regression.holdoutPassed}/{v.regression.holdoutTotal}
            </span>
            {v.regression.holdoutPassed < v.regression.holdoutTotal ? (
              <span className="danger"> · sürüm engelli</span>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>Öğrenilmedi</dt>
          <dd>
            <span className="mono">{v.notLearned}</span> düzeltme henüz fikstür değil
          </dd>
        </div>
      </dl>
      <h3 className="lr-sub">Fikstürler</h3>
      <ul className="lr-list" aria-label="Kökene göre fikstürler">
        {v.fixtures.map((row) => (
          <li key={row.origin} className="lr-list__row">
            <span className="lr-list__name mono">{row.origin}</span>
            <span className="mono">
              {row.passed}/{row.count}
            </span>
          </li>
        ))}
      </ul>
      <h3 className="lr-sub">Ağ geçidi geri dönmeleri</h3>
      {v.gateways.length === 0 ? (
        <p className="placeholder-text">Gönderim yok; ağ geçidi verisi gönderim açılınca gelir.</p>
      ) : (
        <ul className="lr-list" aria-label="Ağ geçitleri">
          {v.gateways.map((row) => (
            <li key={row.gateway} className="lr-list__row">
              <span className="lr-list__name">{row.gateway}</span>
              <span className="mono ink-2">
                {row.delivered} teslim · {row.bounced} geri döndü
              </span>
              <EstimateValue estimate={row.okRate} unit="share" compact />
            </li>
          ))}
        </ul>
      )}
    </Block>
  );
}

// ---- 9. mail ----------------------------------------------------------------

const EXPERIMENT_STATUS: Record<'running' | 'decided' | 'inconclusive', string> = {
  running: 'sürüyor',
  decided: 'karar verildi',
  inconclusive: 'sonuçsuz',
};

export function MailBlock({ view }: { view: LearningView }) {
  const mail = view.mail;
  return (
    <Block id="lr-mail" title="Mail" aside={<LoopKey loop="L5" />} wide>
      {mail.sendingEnabled ? null : (
        <Note>Gönderim kapalı (outreach.sending). Mail ölçümleri gönderim açılınca başlar.</Note>
      )}
      {mail.experiments.length === 0 ? (
        <p className="placeholder-text">Süren deney yok.</p>
      ) : (
        <ul className="lr-list" aria-label="Deneyler">
          {mail.experiments.map((experiment) => (
            <li key={experiment.experimentId} className="lr-experiment">
              <p className="lr-experiment__head">
                <span className="mono">{experiment.experimentId}</span> · {experiment.variable} · {experiment.country} ·
                adım {experiment.step} · {EXPERIMENT_STATUS[experiment.status]} ·{' '}
                <span className="mono">
                  {experiment.weeks}/{experiment.maxWeeks}
                </span>{' '}
                hafta
              </p>
              <ul className="lr-experiment__variants">
                {experiment.variants.map((variant) => (
                  <li key={variant.code}>
                    <span className="mono">{variant.code}</span>
                    {variant.label === undefined ? null : <> · {variant.label}</>} · {variant.delivered} teslim
                    {variant.clickedFirms === undefined ? null : <> · {variant.clickedFirms} tıklayan firma</>}
                    {variant.replies === undefined ? null : <> · {variant.replies} cevap</>} · {variant.positiveReplies}{' '}
                    olumlu · <EstimateValue estimate={variant.rate} unit="share" compact />
                  </li>
                ))}
              </ul>
              <p className="ink-2">
                {experiment.pBetter === null ? (
                  <>P(daha iyi): {NO_ESTIMATE}</>
                ) : (
                  <>
                    P(B daha iyi) <span className="mono">{formatMetric(experiment.pBetter, 'share')}</span>
                    {experiment.expectedLoss !== null ? (
                      <>
                        {' '}
                        · beklenen kayıp <span className="mono">{formatMetric(experiment.expectedLoss, 'share')}</span>
                      </>
                    ) : null}
                  </>
                )}
                {experiment.status === 'inconclusive' ? ' · sonuçsuz: 4 hafta doldu, fark kanıtlanmadı' : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
      <dl className="lr-pairs">
        <div>
          <dt>Düzeltme oranın</dt>
          <dd>
            <EstimateValue estimate={mail.editRate} unit="share" compact />
          </dd>
        </div>
        <div>
          <dt>Denetçi yakalama oranı</dt>
          <dd>
            <EstimateValue estimate={mail.denetciCatchRate} unit="share" compact />
          </dd>
        </div>
        <div>
          <dt>Kör haftalık örnek</dt>
          <dd>
            <span className="mono">{mail.blindSample.reviewed}</span> incelendi ·{' '}
            <span className="mono">{mail.blindSample.flagged}</span> işaretli
          </dd>
        </div>
      </dl>
      <h3 className="lr-sub">Cevap sınıflayıcı</h3>
      {mail.confusion.labels.length === 0 ? (
        <p className="placeholder-text">Cevap yok; karışıklık tablosu ilk cevaplarla gelir.</p>
      ) : (
        <TableWrap label="Cevap sınıflayıcı karışıklık tablosu">
          <table className="lr-table">
            <thead>
              <tr>
                <th scope="col">Gerçek \ sınıf</th>
                {mail.confusion.labels.map((label) => (
                  <th key={label} scope="col" className="num">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mail.confusion.labels.map((label, row) => (
                <tr key={label}>
                  <th scope="row">{label}</th>
                  {mail.confusion.labels.map((column, index) => (
                    <td key={column} className="num mono">
                      {mail.confusion.counts[row]?.[index] ?? 0}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      <h3 className="lr-sub">Otomatik durdurmalar</h3>
      {mail.autoStops.length === 0 ? (
        <p className="placeholder-text">Otomatik durdurma yok.</p>
      ) : (
        <ul className="lr-list" aria-label="Otomatik durdurmalar">
          {mail.autoStops.map((stop) => (
            <li key={`${stop.at}-${stop.what}`} className="lr-list__row">
              <span className="lr-list__name">{stop.what}</span>
              <span className="ink-2">{stop.why}</span>
            </li>
          ))}
        </ul>
      )}
    </Block>
  );
}

// ---- 10. data health --------------------------------------------------------

function flagWord(on: boolean): string {
  return on ? 'açık' : 'kapalı';
}

export function HealthBlock({ view }: { view: LearningView }) {
  const { now } = usePanel();
  const health = view.health;
  return (
    <Block id="lr-saglik" title="Veri sağlığı">
      <dl className="lr-pairs">
        <div>
          <dt>Kaynak bağı (ilk sonuç kimliği olan leadler)</dt>
          <dd>
            <EstimateValue estimate={health.attributionCoverage} unit="share" compact />
          </dd>
        </div>
        <div>
          <dt>Başarısız arama payı</dt>
          <dd>
            <EstimateValue estimate={health.failedSearchShare} unit="share" compact />
          </dd>
        </div>
        <div>
          <dt>Bekleyen ödül</dt>
          <dd>
            <span className="mono">{health.pendingRewards}</span> lead doğrulama bekliyor
          </dd>
        </div>
        <div>
          <dt>Çözülmemiş sonuç</dt>
          <dd className="mono">{health.unresolvedResults}</dd>
        </div>
      </dl>
      <h3 className="lr-sub">Öğrenme işleri</h3>
      <ul className="lr-list" aria-label="Öğrenme işleri">
        {health.staleJobs.map((job) => {
          const late = job.lastRunAt === null || now - job.lastRunAt > job.expectedEveryHours * 1.5 * 60 * 60 * 1000;
          return (
            <li key={job.job} className="lr-list__row">
              <span className="lr-list__name mono">{job.job}</span>
              <span className={late ? 'warning' : 'ink-2'}>
                {job.lastRunAt === null ? 'hiç çalışmadı' : `${formatAge(now - job.lastRunAt)} önce`}
                {late ? ' · gecikti' : ''}
                {job.enabled ? '' : ' · kapalı'}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="lr-flags">
        Anahtarlar: lead taraması {flagWord(view.flags.leadEngine)} · doğrulayıcı {flagWord(view.flags.leadVerifier)} ·
        gönderim {flagWord(view.flags.outreachSending)} · otomatik uygulama {flagWord(view.flags.learningAutoapply)}.{' '}
        <Link to="/ayarlar/politika" className="lr-link">
          Ayarlar › Politika
        </Link>
        'da değişir.
      </p>
    </Block>
  );
}

const FEEDBACK_KIND_LABELS = { edited: 'Düzelttin', rejected: 'Reddettin' } as const;

/** The reply desk's last seven days (v0.1.52): what Sercan approved, edited, rejected and answered himself. */
export function ReplyDeskBlock({ view }: { view: LearningView }) {
  const { now } = usePanel();
  const desk = view.replyDesk;
  return (
    <Block id="lr-cevap" title="Cevap masası" aside={`son ${desk.days} gün`}>
      {desk.drafts === 0 && desk.ownReplies === 0 ? (
        <Note>Henüz veri yok: Temsilci taslak yazdıkça ve sen karar verdikçe burada birikir.</Note>
      ) : null}
      <dl className="lr-pairs">
        <div>
          <dt>Taslak</dt>
          <dd>
            <span className="mono">{desk.drafts}</span> yazıldı, <span className="mono">{desk.pending}</span> onayını
            bekliyor
          </dd>
        </div>
        <div>
          <dt>İlk denemede Denetçi'den geçen</dt>
          <dd>
            <EstimateValue estimate={desk.firstPassRate} unit="share" compact />{' '}
            <span className="ink-2">
              ({desk.passedFirstTime}/{desk.drafts}; {desk.denetciRejected} itiraz)
            </span>
          </dd>
        </div>
        <div>
          <dt>Senin kararın</dt>
          <dd>
            <span className="mono">{desk.approved}</span> olduğu gibi onay, <span className="mono">{desk.edited}</span>{' '}
            düzeltme, <span className="mono">{desk.rejected}</span> ret
          </dd>
        </div>
        <div>
          <dt>Düzeltme payı</dt>
          <dd>
            <EstimateValue estimate={desk.editRate} unit="share" compact />
          </dd>
        </div>
        <div>
          <dt>Kendin cevapladığın</dt>
          <dd className="mono">{desk.ownReplies}</dd>
        </div>
      </dl>
      {desk.rejectedByReason.length > 0 ? (
        <p className="lr-flags">
          Ret gerekçeleri:{' '}
          {desk.rejectedByReason.map((row) => `${REJECT_REASON_LABELS[row.reason]} ${row.n}`).join(' · ')}
        </p>
      ) : null}
      {desk.recent.length > 0 ? (
        <>
          <h3 className="lr-sub">Son kararlar</h3>
          <ul className="lr-list" aria-label="Son kararlar">
            {desk.recent.map((item) => (
              <li key={`${item.kind}-${item.approvalId}`} className="lr-list__row">
                <span className="lr-list__name">
                  {FEEDBACK_KIND_LABELS[item.kind]}: {item.title}
                </span>
                <span className="ink-2">
                  {item.reason === null ? '' : `${REJECT_REASON_LABELS[item.reason]} · `}
                  {item.note === null ? '' : `${item.note} · `}
                  {formatAge(now - item.at)} önce
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Block>
  );
}

/**
 * v0.1.260 (Sercan, 2 Nov 2026): the Temsilci's reply evaluation. Fixed customer scenarios from his corrections; each
 * run writes a draft for every case and checks it. The score, the run before and the cases that fail with the reason.
 */
export function EvalBlock() {
  const { now } = usePanel();
  const { store, state } = useEvals();
  useEffect(() => {
    void store.reload();
  }, [store]);
  const view = state.view;
  const [latest] = view?.runs.filter((run) => run.status === 'completed') ?? [];
  const before = view?.runs.filter((run) => run.status === 'completed')[1];
  const failing = view?.results.filter((result) => result.outcome === 'failed' || result.outcome === 'error') ?? [];
  return (
    <Block id="lr-degerlendirme" title="Temsilci değerlendirmesi" aside="her Pazartesi 06:00">
      {state.error !== null ? <Note tone="warning">Değerlendirme okunamadı: {state.error}</Note> : null}
      {view !== null && latest === undefined ? (
        <Note>Henüz koşu yok: ilk değerlendirme yayından sonra çalışır.</Note>
      ) : null}
      {latest === undefined ? null : (
        <dl className="lr-pairs">
          <div>
            <dt>Son koşu</dt>
            <dd>
              <span className="mono">
                {latest.passed}/{latest.cases - latest.skipped}
              </span>{' '}
              vaka geçti
              {before === undefined ? '' : ` (önceki ${before.passed}/${before.cases - before.skipped})`} ·{' '}
              {formatAge(now - latest.startedAt)} önce · {formatUsd(latest.costUsd)}
            </dd>
          </div>
        </dl>
      )}
      {failing.length > 0 ? (
        <>
          <h3 className="lr-sub">Kalan vakalar</h3>
          <ul className="lr-list" aria-label="Kalan vakalar">
            {failing.map((result) => (
              <li key={result.caseId} className="lr-list__row">
                <span className="lr-list__name">
                  {result.caseId}
                  {result.previous === 'passed' ? ' (yeni)' : ''}: {EVAL_OUTCOME_LABELS[result.outcome]}
                </span>
                <span className="ink-2">{result.failures.map((failure) => failure.whyTr).join(' · ')}</span>
                {result.draft === null ? null : (
                  <details>
                    <summary>Taslak</summary>
                    <p className="lr-flags">{result.draft}</p>
                  </details>
                )}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Block>
  );
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * "Bu hafta öğrendik" (panel audit 25 Oct 2026, F12): three plain sentences before any statistic. What the loop
 * stopped or reduced on its own (class A1), what it started inside the approved limits (A2) or waits for Sercan's
 * yes (the weekly batch), and the effect on the first scorecard tile, qualified firms per 10 searches.
 */
export function learningSentences(view: LearningView): { stopped: string; started: string; effect: string } {
  const recent = view.changes.filter((change) => change.revertedAt === null && view.asOf - change.appliedAt <= WEEK_MS);
  const reduced = recent.filter((change) => change.changeClass === 'A1').length;
  const raised = recent.filter((change) => change.changeClass === 'A2').length;
  const waiting = view.batch?.proposals.length ?? 0;
  const stopped =
    reduced === 0
      ? 'Bu hafta kendiliğinden durdurulan ya da azaltılan bir şey yok.'
      : `Bu hafta sonuç vermeyen ${reduced} şeyi kendiliğinden azalttık ya da durdurduk.`;
  const startedParts = [
    raised === 0 ? null : `onaylı sınırlar içinde ${raised} şeyi artırdık`,
    waiting === 0 ? null : `${waiting} yeni deneme onayını bekliyor`,
  ].filter((part): part is string => part !== null);
  const started =
    startedParts.length === 0
      ? 'Yeni bir şey başlatılmadı.'
      : `${startedParts.join('; ').replace(/^./, (first) => first.toLocaleUpperCase('tr-TR'))}.`;
  const tile = view.scorecard.find((item) => item.id === 'qualified_per_10_queries');
  const value = tile?.estimate.value ?? null;
  const before = tile?.trend.filter((point): point is number => point !== null).at(-1) ?? null;
  const n = tile?.estimate.n ?? 0;
  const one = (number: number) => number.toLocaleString('tr-TR', { maximumFractionDigits: 1 });
  const effect =
    value === null
      ? `Etkisi henüz ölçülemiyor: bu hafta ${n} arama var, ölçmek için en az ${tile?.estimate.minN ?? 20} gerekir.`
      : `Etkisi: 10 aramada ${one(value)} nitelikli firma${before === null ? '' : ` (önceki hafta ${one(before)})`}.`;
  return { stopped, started, effect };
}

export function LearningSummary({ view }: { view: LearningView }) {
  const sentences = learningSentences(view);
  return (
    <section className="lr-summary drafting" aria-labelledby="lr-summary-h">
      <h2 id="lr-summary-h" className="lr-summary__title">
        Bu hafta öğrendik
      </h2>
      <ul className="lr-summary__list">
        <li>{sentences.stopped}</li>
        <li>{sentences.started}</li>
        <li>{sentences.effect}</li>
      </ul>
    </section>
  );
}

/**
 * The Öğrenme tab: the three sentences, then every block behind "Ayrıntı" (the statistics stay for when Sercan
 * wants them, they no longer lead the page).
 */
export function LearningPage({ view }: { view: LearningView }) {
  return (
    <>
      <LearningSummary view={view} />
      <details className="lr-detail">
        <summary className="lr-detail__summary">
          <Icon name="chevron-down" size={16} />
          Ayrıntı: karne, değişiklikler, kollar, terimler, hücreler, sağlık
        </summary>
        <LearningBlocks view={view} />
      </details>
    </>
  );
}

/** All ten blocks in the design's order. */
export function LearningBlocks({ view }: { view: LearningView }) {
  return (
    <div className="lr-grid">
      <ScorecardBlock view={view} />
      <ReplyDeskBlock view={view} />
      <EvalBlock />
      <ChangesBlock view={view} />
      <PendingBlock view={view} />
      <ArmsBlock view={view} />
      <VocabularyBlock view={view} />
      <CellsBlock view={view} />
      <FitBlock view={view} />
      <VerificationBlock view={view} />
      <MailBlock view={view} />
      <HealthBlock view={view} />
    </div>
  );
}
